/* ==========================================================
   SURF NAKED PXM — Video Gallery Frame
   Save to: assets/video-gallery-frame.js
   ========================================================== */

if (!customElements.get('vgf-gallery')) {
  class VgfGallery extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.vgf__slide'));
      this.dots = Array.from(this.querySelectorAll('.vgf__dot'));
      this.index = 0;
      this.timer = null;
      this.paused = false;

      if (this.slides.length < 2) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
      this.speed = parseInt(this.dataset.speed, 10) || 5000;

      // Arrows & dots
      this.querySelector('[data-vgf-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-vgf-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

      // Keyboard
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1, true); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1, true); }
      });

      // Swipe on the frame
      const frame = this.querySelector('.vgf__frame');
      let startX = null;
      let startY = null;
      frame.addEventListener('pointerdown', (e) => { startX = e.clientX; startY = e.clientY; });
      frame.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          this.go(this.index + (dx < 0 ? 1 : -1), true);
        }
      });
      frame.addEventListener('pointercancel', () => { startX = null; });

      // Pause on hover / focus
      this.addEventListener('mouseenter', () => { this.paused = true; this.stop(); });
      this.addEventListener('mouseleave', () => { this.paused = false; this.start(); });
      this.addEventListener('focusin', () => { this.paused = true; this.stop(); });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) { this.paused = false; this.start(); }
      });

      // Theme editor: jump to the block being edited
      this.onBlockSelect = (e) => {
        const i = this.slides.indexOf(e.target);
        if (i === -1) return;
        this.paused = true;
        this.stop();
        this.go(i);
      };
      this.onBlockDeselect = (e) => {
        if (this.slides.indexOf(e.target) === -1) return;
        this.paused = false;
        this.start();
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }
    }

    go(i, fromUser = false) {
      const n = this.slides.length;
      const next = ((i % n) + n) % n;

      this.slides.forEach((slide, idx) => {
        const active = idx === next;
        slide.classList.toggle('is-active', active);
        slide.toggleAttribute('inert', !active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.dots.forEach((dot, idx) => {
        const active = idx === next;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      this.index = next;
      if (fromUser) { this.stop(); this.start(); }
    }

    start() {
      if (!this.autoplay || this.paused || this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  customElements.define('vgf-gallery', VgfGallery);
}
